import React, { type PropsWithChildren } from 'react';

import {
	type HeadingLevel,
	type SurfaceModule,
	type SurfaceModuleParameters,
} from '../../common/types';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import ErrorBoundary from '../../ui/error-boundary';
import { type ActionDeclaration } from '../actions/types';

import { type SurfaceRenderer, type SurfaceRendererProps, type ZonesRendererProps } from './types';

const createSurfaceElement = <Props extends Record<string, any>>(
	type: SurfaceModule<Props, Props>,
	props: SurfaceModuleParameters<Props, any> & Props,
	...children: React.ReactNode[]
) => React.createElement(type, props, children);

const incrementBaseHeadingLevel = (baseHeadingLevel: HeadingLevel): HeadingLevel => {
	const number: number = Number(baseHeadingLevel.replace('h', ''));
	return `h${Math.min(number + 1, 6)}` as HeadingLevel;
};

export const createSurfaceRenderer = <ModuleName extends string>(
	moduleDictionary: Record<ModuleName, SurfaceModule<any>>,
): SurfaceRenderer => {
	const moduleMap = new Map<string, SurfaceModule>(Object.entries(moduleDictionary));

	const ZoneRenderer = <Actions extends ActionDeclaration>({
		zones,
		createMutateAtModule,
		updateId,
		headingLevel,
		createActionBusAtModule,
		context,
	}: ZonesRendererProps<Actions>) => (
		<>
			{zones &&
				Object.values(zones).flatMap((selectionItems) =>
					(selectionItems ?? []).map((selectionItem, i): React.ReactNode => {
						const module = selectionItem.data;

						if (module === null || !module?.type || !module?.key) {
							return null;
						}

						// Looks up imported modules, which are (kind of) similar to components and uses that if available
						const componentToRender = moduleMap.get(module.type);

						if (!componentToRender) {
							throw new Error(
								`Surface Renderer tried to render a component of type ${module?.type} but no such component exists`,
							);
						}

						const mutate = createMutateAtModule(module.type, module.key);

						return createSurfaceElement(
							componentToRender,
							{
								...module?.fields,
								id: module.key,
								key: [module.type, module.key, i].join('-'),
								actionBus: createActionBusAtModule(selectionItem),
								context,
								mutate,
								headingLevel,
							},
							<ZoneRenderer
								key={`${Object.keys(zones ?? []).join('-')}-zones `}
								updateId={updateId}
								zones={module?.zones}
								createMutateAtModule={createMutateAtModule}
								headingLevel={incrementBaseHeadingLevel(headingLevel)}
								createActionBusAtModule={createActionBusAtModule}
								context={context}
							/>,
						);
					}),
				)}
		</>
	);

	const FallbackHandler = ({
		error,
		isLoading,
		surface,
		fallback,
		children,
	}: PropsWithChildren<
		Pick<SurfaceRendererProps<any>, 'error' | 'isLoading' | 'fallback' | 'surface'>
	>) => {
		if (error) {
			if (!error.name || error.name === '') {
				throw new Error(
					`Unnamed error in surface renderer. isLoading: ${isLoading}, surfaceExists: ${
						surface !== undefined
					}, stack: ${error.stack}`,
				);
			}
			throw error;
		}

		if (isLoading) {
			return <>{fallback}</>;
		}

		if (!isLoading && !surface) {
			return null;
		}

		return <>{children}</>;
	};

	const SurfaceRenderer: SurfaceRenderer = ({
		surface,
		isLoading,
		fallback,
		error,
		errorFallback = null,
		createMutateAtModule,
		updateId,
		baseHeadingLevel,
		createActionBusAtModule,
		context,
	}) => (
		<ErrorBoundary fallback={errorFallback}>
			<FallbackHandler surface={surface} error={error} isLoading={isLoading} fallback={fallback}>
				<ZoneRenderer
					key={`${Object.keys(surface?.zones ?? []).join('-')}-zones `}
					updateId={updateId}
					zones={surface?.zones}
					createMutateAtModule={createMutateAtModule}
					headingLevel={baseHeadingLevel}
					createActionBusAtModule={createActionBusAtModule}
					context={context}
				/>
			</FallbackHandler>
		</ErrorBoundary>
	);

	return SurfaceRenderer;
};
